import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '@styles/common/Loading.module.css';
import Snackbar from './Snackbar';

interface LoadingProps {
  message?: string;
  fullPage?: boolean;
  className?: string;
}

/**
 * Loading - A reusable loading component with spinner
 *
 * @param message - Optional loading message (default: "Loading...")
 * @param fullPage - Whether to render as full page (default: true)
 * @param className - Optional additional CSS class
 *
 * @example
 * <Loading />
 *
 * @example
 * <Loading message="Fetching data..." fullPage={false} />
 */
const Loading: React.FC<LoadingProps> = ({
  message,
  fullPage = true,
  className,
}) => {
  const { t } = useTranslation();
  const displayMessage = message || t('loading.default');
  // When fullPage is false, show as snackbar overlay in center
  if (!fullPage) {
    return (
      <Snackbar
        message={displayMessage}
        variant='info'
        position='center'
        dismissable={false}
        duration='permanent'
        showSpinner={true}
        className={className}
      />
    );
  }

  const containerClass = `${styles.fullPageContainer} ${className || ''}`;

  return (
    <div className={containerClass} role='status' aria-live='polite'>
      <div className={styles.spinner}></div>
      <span className={styles.message}>{displayMessage}</span>
    </div>
  );
};

export default Loading;
